import type { User } from '@auth0/nextjs-auth0/types'
import { UserRole } from '@/constants/enums'

/**
 * JWT/session claim key for app roles (Auth0 custom claim namespace).
 * Empty string if env is unset (callers should treat as no roles from claim).
 */
export function getAppRoleClaimKey(): string {
    return process.env['NEXT_PUBLIC_APP_ROLE_BASE_URL']?.trim() ?? ''
}

/**
 * Normalizes Auth0 role claim values to a string array.
 * Handles array, single string, null/undefined, and filters invalid entries.
 */
export function normalizeAppRoles(claim: unknown): string[] {
    if (claim == null) {
        return []
    }
    if (typeof claim === 'string') {
        const t = claim.trim()
        return t ? [t] : []
    }
    if (Array.isArray(claim)) {
        return claim
            .filter((r): r is string => typeof r === 'string')
            .map((r) => r.trim())
            .filter(Boolean)
    }
    return []
}

/**
 * Reads normalized app roles from a session/JWT user object.
 */
export function getAppRolesFromUser(user: User | Record<string, unknown> | null | undefined): string[] {
    const key = getAppRoleClaimKey()
    if (!key || !user) {
        return []
    }
    return normalizeAppRoles((user as Record<string, unknown>)[key])
}

export function hasAppRole(roles: readonly string[], role: string): boolean {
    return roles.includes(role)
}

export function hasAnyAppRole(roles: readonly string[], candidates: readonly string[]): boolean {
    return candidates.some((r) => roles.includes(r))
}

/**
 * Default ordering: highest-privilege first for picking a single label in UI.
 * Aligns with common RBAC expectations (not the company user list `type` tie-break).
 */
export const DEFAULT_PRIMARY_ROLE_PRIORITY: readonly string[] = [
    UserRole.SuperAdmin,
    UserRole.Admin,
    UserRole.HRHead,
    UserRole.TalentLead,
    UserRole.TalentManager,
    UserRole.Recruiter,
    UserRole.Manager,
    UserRole.Employee,
    UserRole.Panellist,
    UserRole.External,
] as const

/**
 * Same priority as GET company users `type` field (Manager before Admin for display).
 */
export const COMPANY_USER_TYPE_ROLE_PRIORITY: readonly string[] = [UserRole.Manager, UserRole.Admin, UserRole.Recruiter, UserRole.Panellist] as const

/**
 * First role from `priority` that the user has; if none match, returns first entry in `roles` (legacy `roles[0]` behavior).
 */
export function getPrimaryAppRole(roles: readonly string[], priority: readonly string[] = DEFAULT_PRIMARY_ROLE_PRIORITY): string | undefined {
    if (roles.length === 0) {
        return undefined
    }
    for (const p of priority) {
        if (roles.includes(p)) {
            return p
        }
    }
    return roles[0]
}
